import { useMutation } from '@tanstack/react-query'
import {
  type DeleteSuggestionPayload,
  type SearchEngagement,
  type SearchSuggestions,
  type SuggestionsResponse,
} from '@/features/shared/services/Autocomplete/types'
import { normalizeSuggestions } from '@/features/shop/services/Search/utils'
import { apiDelete, apiPost } from '@/features/shared/utils/dataFetching'
import {
  fetchMultiRetailerSearchSuggestions,
  fetchSearchSuggestions,
} from '@/features/shared/services/Autocomplete/fetchers'
import { type StoreLocationParams } from '@/features/shared/utils/dataFetching/storeParams'

export const useMutationSearchSuggestions = ({
  store_id,
  store_location_id,
  metro_id,
}: StoreLocationParams) => {
  return useMutation({
    mutationFn: (query: SearchSuggestions) =>
      fetchSearchSuggestions(query, {
        store_id,
        store_location_id,
        metro_id,
      }),
  })
}

export const useMutationGlobalSearchSuggestions = (metro_id: number) => {
  return useMutation({
    mutationFn: (query: SearchSuggestions) => fetchMultiRetailerSearchSuggestions(query, metro_id),
  })
}

export const useMutateSearchEngagement = ({
  store_id,
  store_location_id,
  metro_id,
}: StoreLocationParams) => {
  return useMutation({
    mutationFn: (payload: SearchEngagement) =>
      apiPost<void>({
        config: {
          url: 'autocomplete/v1/engagement',
          params: { store_id, store_location_id, metro_id },
          data: payload,
        },
        fetcherName: 'useMutateSearchEngagement',
      }),
  })
}

export const useMutationDeletePersonalizedSuggestions = ({
  isMultiStoreSearch = false,
  storeId,
}: {
  isMultiStoreSearch?: boolean
  storeId: number
}) => {
  const url = isMultiStoreSearch
    ? 'autocomplete/v2/multi-retailer/suggestions/personalized'
    : 'autocomplete/v1/suggestions/personalized'

  return useMutation({
    mutationFn: async (payload: DeleteSuggestionPayload) => {
      const data = await apiDelete<SuggestionsResponse>({
        config: {
          url,
          data: {
            ...payload,
            store_id: String(storeId),
          },
        },
        fetcherName: 'useMutationDeletePersonalizedSuggestions',
      })
      return normalizeSuggestions(data)
    },
  })
}
